import { useEffect, useState } from "react";
import { supabase } from "@/lib/supabase";
import type { CrewMember, CrewStatus } from "@/mocks/admin/crew";

type Props = {
  open: boolean;
  member: CrewMember;
  onClose: () => void;
  onSaved?: () => void;
};

const roleOptions = ["Lead Mover", "Mover", "Driver", "Mover / Driver"];

const statusOptions: { key: CrewStatus; label: string }[] = [
  { key: "active", label: "Active" },
  { key: "training", label: "Training" },
  { key: "off", label: "Off" },
  { key: "on-leave", label: "On Leave" },
];

const skillOptions = [
  "Piano",
  "Antiques",
  "Packing",
  "Heavy lift",
  "CDL-A",
  "CDL-B",
  "CDL-C",
  "Long Distance",
  "Commercial",
  "Office",
  "Electronics",
  "Inventory",
  "Lead",
  "Driver",
  "Trainee",
];

const DAYS: ("M" | "T" | "W" | "Th" | "F" | "Sa" | "Su")[] = [
  "M",
  "T",
  "W",
  "Th",
  "F",
  "Sa",
  "Su",
];

const dayLabels: Record<string, string> = {
  M: "Mon",
  T: "Tue",
  W: "Wed",
  Th: "Thu",
  F: "Fri",
  Sa: "Sat",
  Su: "Sun",
};

const avatarColors = [
  "bg-brand-500",
  "bg-brand-600",
  "bg-indigo-500",
  "bg-pink-500",
  "bg-emerald-500",
  "bg-emerald-600",
  "bg-fuchsia-500",
  "bg-amber-500",
  "bg-amber-600",
  "bg-rose-500",
  "bg-rose-600",
  "bg-teal-500",
  "bg-teal-600",
  "bg-cyan-600",
  "bg-orange-500",
];

function pickInitials(name: string) {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "??";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

export default function EditCrewModal({ open, member, onClose, onSaved }: Props) {
  const [name, setName] = useState(member.name);
  const [initials, setInitials] = useState(member.initials);
  const [color, setColor] = useState(member.color);
  const [role, setRole] = useState(member.role);
  const [email, setEmail] = useState(member.email);
  const [phone, setPhone] = useState(member.phone);
  const [city, setCity] = useState(member.city);
  const [hireDate, setHireDate] = useState(member.hireDate);
  const [hourlyRate, setHourlyRate] = useState<string>(String(member.hourlyRate));
  const [weeklyTarget, setWeeklyTarget] = useState<string>(String(member.weeklyTarget));
  const [status, setStatus] = useState<CrewStatus>(member.status);
  const [skills, setSkills] = useState<string[]>(member.skills);
  const [availability, setAvailability] = useState<string[]>(member.availability);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  // Reset state every time we open with a different member
  useEffect(() => {
    if (open) {
      setName(member.name);
      setInitials(member.initials);
      setColor(member.color);
      setRole(member.role);
      setEmail(member.email);
      setPhone(member.phone);
      setCity(member.city);
      setHireDate(member.hireDate);
      setHourlyRate(String(member.hourlyRate));
      setWeeklyTarget(String(member.weeklyTarget));
      setStatus(member.status);
      setSkills(member.skills || []);
      setAvailability(member.availability || []);
      setError("");
      setSaving(false);
    }
  }, [open, member]);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape" && open) onClose();
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [open, onClose]);

  if (!open) return null;

  const toggleSkill = (s: string) => {
    setSkills((cur) => (cur.includes(s) ? cur.filter((x) => x !== s) : [...cur, s]));
  };

  const toggleDay = (d: string) => {
    setAvailability((cur) => (cur.includes(d) ? cur.filter((x) => x !== d) : [...cur, d]));
  };

  const onSyncInitials = () => setInitials(pickInitials(name));

  const handleSave = async () => {
    setError("");
    if (!name.trim()) {
      setError("Name is required.");
      return;
    }
    const rate = Number(hourlyRate);
    const target = Number(weeklyTarget);
    if (Number.isNaN(rate) || rate < 0) {
      setError("Hourly rate must be a positive number.");
      return;
    }
    if (Number.isNaN(target) || target <= 0) {
      setError("Weekly target must be a positive number.");
      return;
    }

    setSaving(true);
    const cleanedInitials = (initials || pickInitials(name)).slice(0, 3).toUpperCase();

    const { error: upErr } = await supabase
      .from("crew_members")
      .update({
        name: name.trim(),
        initials: cleanedInitials,
        color,
        role,
        email: email.trim() || null,
        phone: phone.trim(),
        city: city.trim(),
        hire_date: hireDate,
        hourly_rate: rate,
        weekly_target: target,
        status,
        skills,
        availability,
      })
      .eq("id", member.id);

    if (upErr) {
      setError(upErr.message);
      setSaving(false);
      return;
    }

    setSaving(false);
    onSaved?.();
    onClose();
  };

  return (
    <>
      <div
        className="fixed inset-0 z-40 bg-ink-900/50 backdrop-blur-sm transition-opacity"
        onClick={onClose}
      />
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none">
        <div
          className="bg-white w-full max-w-2xl rounded-lg border border-ink-100 max-h-[90vh] flex flex-col pointer-events-auto"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="px-6 py-4 border-b border-ink-100 flex items-center justify-between shrink-0">
            <div className="flex items-center gap-3">
              <span className={`w-10 h-10 rounded-md text-white text-sm font-bold flex items-center justify-center ${color}`}>
                {(initials || pickInitials(name) || "??").slice(0, 3)}
              </span>
              <div>
                <h2 className="font-display text-lg font-bold text-ink-900">Edit Profile</h2>
                <div className="text-xs text-ink-500">
                  Updating <span className="font-mono font-bold">{member.id}</span>
                </div>
              </div>
            </div>
            <button
              onClick={onClose}
              className="w-8 h-8 flex items-center justify-center rounded-md hover:bg-cream-100 text-ink-500 cursor-pointer"
              aria-label="Close"
            >
              <i className="ri-close-line text-lg"></i>
            </button>
          </div>

          <div className="flex-1 overflow-y-auto scrollbar-thin px-6 py-5 space-y-5">
            {/* Identity */}
            <section className="space-y-4">
              <SectionTitle icon="ri-user-line" title="Identity" />
              <Field label="Full name" required>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="form-input"
                />
              </Field>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field label="Initials">
                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      maxLength={3}
                      value={initials}
                      onChange={(e) => setInitials(e.target.value.toUpperCase())}
                      className="form-input uppercase"
                    />
                    <button
                      type="button"
                      onClick={onSyncInitials}
                      className="shrink-0 inline-flex items-center gap-1 px-3 py-2.5 rounded-md text-xs font-semibold border border-ink-200 text-ink-700 hover:bg-cream-100 cursor-pointer whitespace-nowrap"
                    >
                      <i className="ri-magic-line"></i>
                      Sync
                    </button>
                  </div>
                </Field>
                <Field label="Avatar color">
                  <div className="flex flex-wrap items-center gap-1.5">
                    {avatarColors.map((c) => {
                      const active = color === c;
                      return (
                        <button
                          key={c}
                          type="button"
                          onClick={() => setColor(c)}
                          aria-label={c}
                          className={`w-8 h-8 rounded-full ${c} flex items-center justify-center text-white text-xs font-bold border-2 cursor-pointer transition-transform ${
                            active ? "border-ink-900 scale-110" : "border-transparent"
                          }`}
                        >
                          {active && <i className="ri-check-line"></i>}
                        </button>
                      );
                    })}
                  </div>
                </Field>
              </div>
            </section>

            {/* Contact */}
            <section className="space-y-4">
              <SectionTitle icon="ri-contacts-line" title="Contact" />
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field label="Phone">
                  <input
                    type="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    className="form-input"
                  />
                </Field>
                <Field label="Email">
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="form-input"
                  />
                </Field>
              </div>
              <Field label="Home city">
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center text-ink-400">
                    <i className="ri-map-pin-line text-sm"></i>
                  </span>
                  <input
                    type="text"
                    value={city}
                    onChange={(e) => setCity(e.target.value)}
                    className="form-input pl-9"
                  />
                </div>
              </Field>
            </section>

            {/* Role & Pay */}
            <section className="space-y-4">
              <SectionTitle icon="ri-briefcase-line" title="Role & pay" />
              <Field label="Role">
                <div className="grid grid-cols-2 gap-2">
                  {roleOptions.map((r) => (
                    <button
                      key={r}
                      type="button"
                      onClick={() => setRole(r)}
                      className={`text-sm font-semibold px-3 py-2.5 rounded-md border cursor-pointer whitespace-nowrap transition-colors ${
                        role === r
                          ? "bg-ink-900 text-white border-ink-900"
                          : "bg-white text-ink-700 border-ink-200 hover:bg-cream-100"
                      }`}
                    >
                      {r}
                    </button>
                  ))}
                </div>
              </Field>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field label="Hire date">
                  <input
                    type="text"
                    value={hireDate}
                    onChange={(e) => setHireDate(e.target.value)}
                    placeholder="e.g. Feb 2022"
                    className="form-input"
                  />
                </Field>
                <Field label="Status">
                  <div className="flex flex-wrap gap-1.5">
                    {statusOptions.map((s) => (
                      <button
                        key={s.key}
                        type="button"
                        onClick={() => setStatus(s.key)}
                        className={`text-xs font-semibold px-3 py-1.5 rounded-full border cursor-pointer whitespace-nowrap transition-colors ${
                          status === s.key
                            ? "bg-ink-900 text-white border-ink-900"
                            : "bg-white text-ink-700 border-ink-200 hover:bg-cream-100"
                        }`}
                      >
                        {s.label}
                      </button>
                    ))}
                  </div>
                </Field>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field label="Hourly rate ($)">
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-400 text-sm">
                      $
                    </span>
                    <input
                      type="number"
                      min={0}
                      step={1}
                      value={hourlyRate}
                      onChange={(e) => setHourlyRate(e.target.value)}
                      className="form-input pl-7"
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-ink-400 text-xs">
                      /hr
                    </span>
                  </div>
                </Field>
                <Field label="Weekly target (hours)">
                  <input
                    type="number"
                    min={1}
                    step={1}
                    value={weeklyTarget}
                    onChange={(e) => setWeeklyTarget(e.target.value)}
                    className="form-input"
                  />
                </Field>
              </div>
            </section>

            {/* Skills & availability */}
            <section className="space-y-4">
              <SectionTitle icon="ri-tools-line" title="Skills & availability" />
              <Field label="Skills">
                <div className="flex flex-wrap gap-1.5">
                  {skillOptions.map((s) => {
                    const active = skills.includes(s);
                    return (
                      <button
                        key={s}
                        type="button"
                        onClick={() => toggleSkill(s)}
                        className={`text-xs font-semibold px-3 py-1.5 rounded-full border cursor-pointer whitespace-nowrap transition-colors ${
                          active
                            ? "bg-emerald-500 text-white border-emerald-500"
                            : "bg-white text-ink-700 border-ink-200 hover:bg-cream-100"
                        }`}
                      >
                        {active && <i className="ri-check-line mr-1"></i>}
                        {s}
                      </button>
                    );
                  })}
                </div>
              </Field>

              <Field label="Available days">
                <div className="flex items-center gap-1.5 flex-wrap">
                  {DAYS.map((d) => {
                    const active = availability.includes(d);
                    return (
                      <button
                        key={d}
                        type="button"
                        onClick={() => toggleDay(d)}
                        className={`w-12 h-10 rounded-md text-xs font-bold cursor-pointer transition-colors ${
                          active
                            ? "bg-brand-100 text-brand-700 border border-brand-300"
                            : "bg-cream-50 text-ink-400 border border-ink-100 hover:bg-cream-100"
                        }`}
                      >
                        {dayLabels[d]}
                      </button>
                    );
                  })}
                </div>
              </Field>
            </section>

            {error && (
              <div className="flex items-start gap-2 bg-rose-50 border border-rose-200 text-rose-700 text-sm px-3 py-2 rounded-md">
                <i className="ri-error-warning-line mt-0.5"></i>
                <span>{error}</span>
              </div>
            )}
          </div>

          <div className="px-6 py-4 border-t border-ink-100 bg-cream-50 flex items-center justify-end gap-2 shrink-0 rounded-b-lg">
            <button
              onClick={onClose}
              className="text-sm font-semibold text-ink-500 hover:text-ink-900 px-3 py-2 cursor-pointer"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={saving || !name.trim()}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md text-sm font-semibold bg-ink-900 hover:bg-ink-800 text-white cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? (
                <>
                  <i className="ri-loader-4-line animate-spin"></i>
                  Saving…
                </>
              ) : (
                <>
                  <i className="ri-save-line"></i>
                  Save changes
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}

function SectionTitle({ icon, title }: { icon: string; title: string }) {
  return (
    <div className="flex items-center gap-2">
      <span className="w-6 h-6 flex items-center justify-center rounded-md bg-cream-100 text-ink-700">
        <i className={`${icon} text-sm`}></i>
      </span>
      <h3 className="text-xs font-bold uppercase tracking-wider text-ink-700">{title}</h3>
      <div className="flex-1 border-t border-ink-100"></div>
    </div>
  );
}

function Field({
  label,
  required,
  children,
}: {
  label: string;
  required?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div>
      <label className="block text-xs font-bold uppercase tracking-wider text-ink-500 mb-1.5">
        {label}
        {required && <span className="text-rose-500 ml-1">*</span>}
      </label>
      {children}
    </div>
  );
}